import {
  Archive,
  BookMarked,
  History,
  ListChecks,
  MessageCircleQuestion,
  CalendarDays,
  FileUp,
  Images,
  Link2,
  MapPin,
  Network,
  Settings,
  UserCog,
  Users,
} from 'lucide-react';
import type { UserRole } from '@familienbaum/shared';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof Users;
  /** Nur für diese Rollen sichtbar; leer = alle. */
  roles?: UserRole[];
  end?: boolean;
  /**
   * Zeigt einen Zähler neben dem Eintrag.
   *
   * `questions` ist gold und rund – dort wartet ein Mensch auf eine Antwort.
   * `gaps` steht in Mono und leise: Lücken warten nicht, sie sind einfach da,
   * und eine goldene Pille an dieser Stelle wäre eine Mahnung ohne Absender.
   */
  badge?: 'questions' | 'gaps' | 'media';
  /**
   * Ein zweiter Weg in denselben Bereich – eingerückt und an einer
   * Führungslinie unter dem Eintrag, zu dem er gehört.
   *
   * Nur dort, wo das Kind wirklich *dieselbe Sache anders angesehen* ist:
   * „Personen“ unter „Stammbaum“ zeigt denselben Bestand als Liste statt als
   * Baum. Für Datenarten, die nebenbei mitgepflegt werden, ist die Werkstatt
   * da und nicht eine Einrückung.
   */
  children?: NavItem[];
}

export interface NavGroup {
  /** Überschrift der Gruppe; `null` für die erste, die keine braucht. */
  label: string | null;
  /**
   * Die Hauptbereiche der Ahnenkiste.
   *
   * Ihre Einträge stehen in vollem Ton (`text-ink`) da, wo die übrigen leise
   * sind – das ist der ganze Unterschied, den die Leiste zwischen „hier lebt
   * das Archiv“ und „hier wird es gepflegt“ macht. Eine zweite Schriftgröße
   * oder eine eigene Fläche wäre eine Auszeichnung zu viel: sie stehen schon
   * oben und ohne Überschrift.
   */
  primary?: boolean;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  /**
   * Die drei Orte, an denen man sich aufhält – ohne Überschrift, weil eine
   * Überschrift den Begriff verschluckt hätte, um den es hier geht.
   *
   * Vorher stand darüber „Album“ als Gruppenname, „Ahnenkiste“ nur als Marke
   * und der Stammbaum als eine Zeile unter „Archiv“ zwischen Orten und
   * Quellen. Damit war der wichtigste Weg des Archivs eine Datenart neben
   * anderen. Jetzt: die Kiste selbst (was diese Woche passiert ist), das
   * Fotoalbum (alle Bilder), der Stammbaum (alle Menschen – als Baum oder als
   * Liste).
   *
   * „Meine Ahnenkiste“ ist ein fester Begriff und nicht der in den
   * Einstellungen vergebene Archivname: der steht oben an der Marke und im
   * Kopf der Startseite. Stünde er auch hier, wäre der Begriff in jedem
   * umbenannten Archiv verschwunden.
   */
  {
    label: null,
    primary: true,
    items: [
      { to: '/', label: 'Meine Ahnenkiste', icon: Archive, end: true },
      { to: '/medien', label: 'Fotoalbum', icon: Images, badge: 'media' },
      {
        to: '/stammbaum',
        label: 'Stammbaum',
        icon: Network,
        children: [{ to: '/personen', label: 'Personen', icon: Users }],
      },
    ],
  },
  /**
   * Was zwischen den Menschen passiert, die mitschreiben: Fragen, auf die
   * jemand wartet, und Lücken, die jemand füllen kann. Beides bekommt später
   * mehr Gewicht – die Überschrift steht schon jetzt da, damit der Ort einen
   * Namen hat, in den das hineinwachsen kann.
   */
  {
    label: 'Familien-Netzwerk',
    items: [
      { to: '/fragen', label: 'Familienfragen', icon: MessageCircleQuestion, badge: 'questions' },
      { to: '/luecken', label: 'Lücken schließen', icon: ListChecks, badge: 'gaps' },
    ],
  },
  /**
   * Die Hilfsdaten – „Werkstatt“ und nicht „Archiv“: hier wird geordnet und
   * belegt, nicht gestöbert. „Archiv“ hätte begrifflich mit der Ahnenkiste
   * darüber konkurriert.
   */
  {
    label: 'Werkstatt',
    items: [
      { to: '/beziehungen', label: 'Beziehungen', icon: Link2 },
      { to: '/ereignisse', label: 'Ereignisse', icon: CalendarDays },
      { to: '/orte', label: 'Orte', icon: MapPin },
      { to: '/quellen', label: 'Quellen', icon: BookMarked },
    ],
  },
  {
    label: 'Verwaltung',
    items: [
      { to: '/aktivitaeten', label: 'Aktivitäten', icon: History, roles: ['admin', 'editor'] },
      { to: '/benutzer', label: 'Benutzer', icon: UserCog, roles: ['admin'] },
      { to: '/import', label: 'Import', icon: FileUp, roles: ['admin'] },
      { to: '/einstellungen', label: 'Einstellungen', icon: Settings },
    ],
  },
];

/** Darf diese Rolle den Eintrag sehen? */
function allowed(item: NavItem, role: UserRole | undefined): boolean {
  return !item.roles || Boolean(role && item.roles.includes(role));
}

export function visibleGroups(role: UserRole | undefined): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => allowed(item, role))
      .map((item) =>
        item.children
          ? { ...item, children: item.children.filter((child) => allowed(child, role)) }
          : item,
      ),
  })).filter((group) => group.items.length > 0);
}
